import React, { useContext, useState, useEffect } from "react";
import { TranslatorContext } from "../../context/Translator";
import { Row, Col, Button, Modal, Form, Alert, Spinner, Accordion, Badge, Table } from "react-bootstrap";
import axios from "axios";
import { FaEdit, FaTrash, FaPlus, FaStethoscope, FaFileAlt, FaPills, FaTrashAlt, FaShoppingCart, FaHome } from "react-icons/fa";
import { MdAccessTime, MdDateRange } from "react-icons/md";
import Swal from 'sweetalert2';

// Duration type options
const DURATION_TYPE_OPTIONS = [
    { value: 'days', label: 'Days' },
    { value: 'weeks', label: 'Weeks' },
    { value: 'months', label: 'Months' }
];

// Time per day options
const TIME_PER_DAY_OPTIONS = [
    { value: 'morning', label: 'Morning' },
    { value: 'afternoon', label: 'Afternoon' },
    { value: 'evening', label: 'Evening' },
    { value: 'morning+afternoon', label: 'Morning + Afternoon' },
    { value: 'morning+evening', label: 'Morning + Evening' },
    { value: 'afternoon+evening', label: 'Afternoon + Evening' },
    { value: 'morning+afternoon+evening', label: 'Morning + Afternoon + Evening' }
];

export default function PetHomePrescriptionComponent({ appointmentId, petId, isAppointmentOpen }) {
    const { t } = useContext(TranslatorContext);
    const [petPrescriptions, setPetPrescriptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingPrescription, setEditingPrescription] = useState(null);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [generalNote, setGeneralNote] = useState("");
    const [openAccordion, setOpenAccordion] = useState(null);
    const [openNestedAccordion, setOpenNestedAccordion] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [hasPrescriptionForAppointment, setHasPrescriptionForAppointment] = useState(false);

    // Get user role from localStorage
    const userRole = localStorage.getItem("role");
    const permissions = JSON.parse(localStorage.getItem("permissions")) || [];

    const hasPermission = (perm) => {
        return permissions.includes(perm);
    };

    // Delete permission only for Admin
    const canDelete = () => {
        return userRole === 'Admin';
    };

    // Edit permission for prescription
    const canEditPrescription = () => {
        return hasPermission("Appointment Prescription edit");
    };

    // Toast notification functions
    const showSuccessToast = (message) => {
        Swal.fire({
            icon: 'success',
            title: t('success'),
            text: message,
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
        });
    };

    const showErrorToast = (message) => {
        Swal.fire({
            icon: 'error',
            title: t('error'),
            text: message,
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
        });
    };

    const showWarningToast = (message) => {
        Swal.fire({
            icon: 'warning',
            title: t('warning'),
            text: message,
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
        });
    };

    useEffect(() => {
        fetchPetPrescriptions();
    }, [petId, appointmentId]);

    const fetchPetPrescriptions = async () => {
        try {
            setLoading(true);
            const AuthToken = localStorage.getItem("authToken");
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/pet-home-prescription/${petId}`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data) {
                const prescriptions = response.data.petPrescription || [];
                setPetPrescriptions(prescriptions);
                const hasPrescription = prescriptions.some(prescription =>
                    prescription.appointment_id === appointmentId || prescription.appointment?.id === appointmentId
                );
                setHasPrescriptionForAppointment(hasPrescription);
                // Set current appointment accordion to open by default
                setOpenAccordion(String(appointmentId));
            }
        } catch (error) {
            console.error("Error fetching pet prescriptions:", error);
            showErrorToast(t('failed_to_load_prescriptions'));
            setPetPrescriptions([]);
        } finally {
            setLoading(false);
        }
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return null;
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return null;
            return {
                date: date.toLocaleDateString(),
                time: date.toLocaleTimeString(),
                full: date.toLocaleString()
            };
        } catch (error) {
            return null;
        }
    };

    const formatTimeDisplay = (timeString) => {
        if (!timeString) return 'N/A';
        try {
            const [hours, minutes] = timeString.split(':');
            let hr = parseInt(hours, 10);
            const ampm = hr >= 12 ? 'PM' : 'AM';
            hr = hr % 12 || 12;
            return `${hr}:${minutes} ${ampm}`;
        } catch (error) {
            return 'N/A';
        }
    };

    const handleAddPrescription = () => {
        if (!canEditPrescription()) {
            showWarningToast(t('you_dont_have_permission_to_add'));
            return;
        }
        setEditingPrescription(null);
        setSelectedProducts([]);
        setGeneralNote("");
        setShowModal(true);
    };

    const handleEditPrescription = (prescription) => {
        if (!canEditPrescription()) {
            showWarningToast(t('you_dont_have_permission_to_edit'));
            return;
        }
        setEditingPrescription(prescription);

        // Map existing prescription details to the format expected by the form
        const existingProducts = prescription.prescription_detail?.map((detail, index) => ({
            id: detail.id, // This is the pet_prescription_detail id for updates
            name: detail.product?.product?.name || detail.product?.name || detail.inhouse_product_name || '',
            quantity: detail.quantity,
            duration: detail.duration || 1,
            duration_type: detail.duration_type || 'days',
            time_per_day: detail.time_per_day || 'morning',
            in_prescription: detail.in_prescription ? 1 : 0
        })) || [];

        setSelectedProducts(existingProducts);

        if (prescription.note && Array.isArray(prescription.note) && prescription.note.length > 0) {
            setGeneralNote(prescription.note[0].note || "");
        } else {
            setGeneralNote("");
        }

        setShowModal(true);
    };

    const handleDeletePrescription = async (prescriptionId) => {
        if (!canDelete()) {
            showWarningToast(t('only_admin_can_delete'));
            return;
        }

        try {
            setSubmitting(true);
            const AuthToken = localStorage.getItem("authToken");
            const response = await axios.delete(
                `${process.env.REACT_APP_API_URL}/pet-home-prescription-delete/${prescriptionId}`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data) {
                fetchPetPrescriptions();
                showSuccessToast(t('Prescription deleted successfully!'));
            }
        } catch (error) {
            console.error("Error deleting prescription:", error);
            showErrorToast(t('failed_to_delete_prescription'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteClick = (prescriptionId) => {
        Swal.fire({
            title: t('Are you sure?'),
            text: t("You won't be able to revert this!"),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('Yes, delete it!'),
            cancelButtonText: t('Cancel'),
        }).then((result) => {
            if (result.isConfirmed) {
                handleDeletePrescription(prescriptionId);
            }
        });
    };

    const handleAddNewRow = () => {
        const newId = Date.now();
        setSelectedProducts([...selectedProducts, {
            id: newId,
            name: '',
            quantity: 1,
            duration: 1,
            duration_type: 'days',
            time_per_day: 'morning',
            in_prescription: 1,
            isNew: true
        }]);
    };

    const handleUpdateProductName = (productId, name) => {
        setSelectedProducts(selectedProducts.map(p =>
            p.id === productId ? { ...p, name: name } : p
        ));
    };

    const handleUpdateQuantity = (productId, newQuantity) => {
        if (newQuantity < 1) {
            setSelectedProducts(selectedProducts.filter(p => p.id !== productId));
        } else {
            setSelectedProducts(selectedProducts.map(p =>
                p.id === productId ? { ...p, quantity: newQuantity } : p
            ));
        }
    };

    const handleRemoveProduct = (productId) => {
        setSelectedProducts(selectedProducts.filter(p => p.id !== productId));
    };

    const handleUpdateProductField = (productId, field, value) => {
        setSelectedProducts(selectedProducts.map(p =>
            p.id === productId ? { ...p, [field]: value } : p
        ));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Filter out products without name
        const validProducts = selectedProducts.filter(p => p.name && p.name.trim() !== '');

        if (validProducts.length === 0) {
            showWarningToast(t('please_add_at_least_one_product'));
            return;
        }

        setSubmitting(true);

        try {
            const AuthToken = localStorage.getItem("authToken");
            const url = editingPrescription
                ? `${process.env.REACT_APP_API_URL}/pet-home-prescription-update/${editingPrescription.id}`
                : `${process.env.REACT_APP_API_URL}/pet-home-prescription`;

            // Prepare payload matching backend expectations
            const payload = {
                appointment_id: appointmentId,
                product: validProducts.map(p => ({
                    id: p.id && !p.isNew ? p.id : undefined, // Only include id for existing records (update)
                    name: p.name,
                    quantity: p.quantity,
                    duration: p.duration,
                    duration_type: p.duration_type,
                    time_per_day: p.time_per_day,
                    in_prescription: p.in_prescription ? 1 : 0
                }))
            };

            if (generalNote && generalNote.trim()) {
                payload.note = generalNote.trim();
            }

            const response = await axios({
                method: 'post',
                url: url,
                data: payload,
                headers: { Authorization: `Bearer ${AuthToken}` }
            });

            if (response.data) {
                setShowModal(false);
                fetchPetPrescriptions();
                showSuccessToast(editingPrescription ? t('Prescription updated successfully!') : t('Prescription added successfully!'));
            }
        } catch (error) {
            console.error("Error saving prescription:", error);
            showErrorToast(error.response?.data?.message || t('failed_to_save_prescription'));
        } finally {
            setSubmitting(false);
        }
    };

    const getDurationTypeLabel = (value) => {
        const option = DURATION_TYPE_OPTIONS.find(opt => opt.value === value);
        return option ? option.label : value;
    };

    const getTimePerDayLabel = (value) => {
        const option = TIME_PER_DAY_OPTIONS.find(opt => opt.value === value);
        return option ? option.label : value;
    };

    const groupPrescriptionsByAppointment = () => {
        const grouped = {};
        petPrescriptions.forEach(prescription => {
            const appointmentIdKey = prescription.appointment_id || prescription.appointment?.id;
            if (!grouped[appointmentIdKey]) {
                grouped[appointmentIdKey] = {
                    appointment: prescription.appointment,
                    prescriptions: []
                };
            }
            grouped[appointmentIdKey].prescriptions.push(prescription);
        });

        const sortedEntries = Object.entries(grouped).sort((a, b) => {
            const aId = String(a[0]);
            const bId = String(b[0]);
            const currentId = String(appointmentId);

            if (aId === currentId) return -1;
            if (bId === currentId) return 1;
            return 0;
        });

        return Object.fromEntries(sortedEntries);
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 mb-0">{t('loading prescriptions')}</p>
            </div>
        );
    }

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
                <h6 className="mb-0 text-muted d-flex align-items-center gap-2">
                    <FaHome className="text-primary" />
                    {t('home prescriptions list')}
                </h6>
                {isAppointmentOpen && !hasPrescriptionForAppointment &&
                    hasPermission("Appointment Prescription Create") && canEditPrescription() && (
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={handleAddPrescription}
                            className="d-flex align-items-center gap-2"
                        >
                            <FaPlus size={12} /> {t('add home prescription')}
                        </Button>
                    )}
            </div>

            {!petPrescriptions || petPrescriptions.length === 0 ? (
                <Alert variant="info" className="text-center py-4">
                    <FaPills size={30} className="mb-2 text-muted" />
                    <p className="mb-0">{t('no home prescriptions found')}</p>
                </Alert>
            ) : (
                <Accordion
                    activeKey={openAccordion}
                    onSelect={(key) => setOpenAccordion(key)}
                    className="pet-prescriptions-accordion"
                >
                    {Object.entries(groupPrescriptionsByAppointment()).map(([appointmentIdKey, appointmentData]) => {
                        const { appointment, prescriptions: appointmentPrescriptions } = appointmentData;
                        const isCurrentAppointment = String(appointmentIdKey) === String(appointmentId);

                        return (
                            <Accordion.Item eventKey={appointmentIdKey} key={appointmentIdKey} className="mb-3 border rounded">
                                <Accordion.Header className="bg-light">
                                    <div className="d-flex justify-content-between align-items-center w-100 me-3 pe-3">
                                        <div className="d-flex flex-column flex-md-row align-items-md-center gap-2 gap-md-3">
                                            <div className="text-muted small">
                                                <strong className="text-primary">{appointment?.appointment_number || 'Unknown Appointment'}</strong>
                                            </div>
                                            <div className="text-muted small">
                                                👨‍⚕️ <strong>Doctor:</strong> {appointment?.doctor?.user?.name || 'Unknown Doctor'}
                                            </div>
                                            <Badge bg="info" className="px-3 py-2">
                                                {appointmentPrescriptions.length} {t('prescription(s)')}
                                            </Badge>
                                        </div>
                                    </div>
                                </Accordion.Header>
                                <Accordion.Body className="bg-white p-0">
                                    <Accordion
                                        activeKey={openNestedAccordion[appointmentIdKey]}
                                        onSelect={(key) => setOpenNestedAccordion({
                                            ...openNestedAccordion,
                                            [appointmentIdKey]: key
                                        })}
                                        className="nested-prescriptions-accordion pe-3 ps-3 pt-3"
                                    >
                                        {appointmentPrescriptions.map((prescription) => {
                                            if (!prescription) return null;
                                            const dateTime = formatDateTime(prescription.created_at);
                                            const noteText = prescription.note && Array.isArray(prescription.note) && prescription.note.length > 0
                                                ? prescription.note[0].note
                                                : null;

                                            return (
                                                <Accordion.Item eventKey={String(prescription.id)} key={prescription.id} className="mb-3 border rounded">
                                                    <Accordion.Header className="bg-light">
                                                        <div className="d-flex justify-content-between align-items-center w-100 me-3 pe-3">
                                                            <div className="d-flex flex-column flex-md-row align-items-md-center gap-2 gap-md-3">
                                                                <FaStethoscope className="text-primary" />
                                                                <Badge bg="info" className="px-3 py-2">
                                                                    {prescription.prescription_detail?.length || 0} {t('items')}
                                                                </Badge>
                                                                {dateTime && (
                                                                    <div className="d-flex align-items-center gap-3 text-muted small">
                                                                        <span className="d-flex align-items-center gap-1">
                                                                            <MdDateRange size={14} />
                                                                            {dateTime.date}
                                                                        </span>
                                                                        <span className="d-flex align-items-center gap-1">
                                                                            <MdAccessTime size={14} />
                                                                            {formatTimeDisplay(dateTime.time)}
                                                                        </span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="d-flex gap-2" onClick={(e) => e.stopPropagation()}>
                                                                {canEditPrescription() && (
                                                                    <Button
                                                                        variant="outline-primary"
                                                                        size="sm"
                                                                        onClick={() => handleEditPrescription(prescription)}
                                                                        className="d-flex align-items-center gap-1"
                                                                        style={{ background: '#3182ce', color: '#fff' }}
                                                                    >
                                                                        <FaEdit size={12} />
                                                                        <span className="d-none d-sm-inline">{t('edit')}</span>
                                                                    </Button>
                                                                )}
                                                                {canDelete() && (
                                                                    <Button
                                                                        variant="danger"
                                                                        size="sm"
                                                                        onClick={() => handleDeleteClick(prescription.id)}
                                                                        className="d-flex align-items-center gap-1"
                                                                    >
                                                                        <FaTrash size={12} />
                                                                        <span className="d-none d-sm-inline">{t('delete')}</span>
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </Accordion.Header>
                                                    <Accordion.Body className="bg-white">
                                                        <Row className="g-4">
                                                            <Col md={12}>
                                                               
                                                                <div className="p-3 bg-light rounded" style={{ overflowX: 'auto' }}>
                                                                    <Table striped bordered hover responsive>
                                                                        <thead>
                                                                            <tr>
                                                                                <th>{t('Product Name')}</th>
                                                                                <th>{t('Quantity')}</th>
                                                                                <th>{t('Duration')}</th>
                                                                                <th>{t('Time Per Day')}</th>
                                                                                <th>{t('Show')}</th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {prescription.prescription_detail?.map((detail, idx) => (
                                                                                <tr key={idx}>
                                                                                    <td>{detail.product?.product?.name || detail.product?.name || detail.inhouse_product_name || 'N/A'}</td>
                                                                                    <td>{detail.quantity}</td>
                                                                                    <td>{detail.duration} {getDurationTypeLabel(detail.duration_type)}</td>
                                                                                    <td>{getTimePerDayLabel(detail.time_per_day)}</td>
                                                                                    <td>{detail.in_prescription ? t('Yes') : t('No')}</td>
                                                                                </tr>
                                                                            ))}
                                                                        </tbody>
                                                                    </Table>

                                                                    {noteText && (
                                                                        <div className="mt-3 pt-2 border-top">
                                                                            <div className="d-flex align-items-center gap-2">
                                                                                <FaFileAlt className="text-success" />
                                                                                <strong className="text-muted">{t('note')}:</strong>
                                                                            </div>
                                                                            <p className="mb-0 mt-1 text-secondary">{noteText}</p>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </Col>
                                                        </Row>
                                                    </Accordion.Body>
                                                </Accordion.Item>
                                            );
                                        })}
                                    </Accordion>
                                </Accordion.Body>
                            </Accordion.Item>
                        );
                    })}
                </Accordion>
            )}

            {/* Add/Edit Modal - Manual entry for product name */}
            <Modal show={showModal} onHide={() => setShowModal(false)} size="xl" style={{ '--bs-modal-width': '1200px' }}>
                <Modal.Header closeButton className="p-2">
                    <Modal.Title>
                        {editingPrescription ? t('Edit Home Prescription') : t('Add Home Prescription')}
                    </Modal.Title>
                </Modal.Header>

                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        {/* Selected Products Table */}
                        <div className="mb-4">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6 className="fw-bold mb-0">
                                    <FaPills className="text-success me-2" />
                                    {t('Prescription Items')}
                                </h6>
                                <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={handleAddNewRow}
                                >
                                    <FaPlus size={12} className="me-1" /> {t('Add Medicine')}
                                </Button>
                            </div>

                            <div style={{ overflowX: 'auto' }}>
                                <Table bordered hover responsive>
                                    <thead className="table-light">
                                        <tr>
                                            <th style={{ width: '30%' }}>{t('Medicine Name')}</th>
                                            <th style={{ width: '10%' }}>{t('Quantity')}</th>
                                            <th style={{ width: '20%' }}>{t('Duration')}</th>
                                            <th style={{ width: '25%' }}>{t('Time Per Day')}</th>
                                            <th style={{ width: '10%' }}>{t('Show')}</th>
                                            <th style={{ width: '5%' }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedProducts.length === 0 ? (
                                            <tr>
                                                <td colSpan="6" className="text-center text-muted py-4">
                                                    <FaShoppingCart size={30} className="mb-2 d-block mx-auto" />
                                                    {t('No products added. Click "Add Medicine" to add items.')}
                                                </td>
                                            </tr>
                                        ) : (
                                            selectedProducts.map((product) => (
                                                <tr key={product.id}>
                                                    <td>
                                                        <Form.Control
                                                            type="text"
                                                            size="sm"
                                                            value={product.name}
                                                            onChange={(e) => handleUpdateProductName(product.id, e.target.value)}
                                                            placeholder={t('Enter medicine name...')}
                                                            required
                                                        />
                                                    </td>
                                                    <td>
                                                        <Form.Control
                                                            type="number"
                                                            size="sm"
                                                            min="1"
                                                            value={product.quantity || ''}
                                                            onChange={(e) => handleUpdateQuantity(product.id, parseInt(e.target.value) || 1)}
                                                            onClick={(e) => e.target.select()}
                                                            style={{ textAlign: 'center' }}
                                                            required
                                                        />
                                                    </td>
                                                    <td>
                                                        <div className="d-flex gap-1">
                                                            <Form.Control
                                                                type="number"
                                                                size="sm"
                                                                min="1"
                                                                value={product.duration || 1}
                                                                onClick={(e) => e.target.select()}
                                                                onChange={(e) => handleUpdateProductField(product.id, 'duration', parseInt(e.target.value) || 1)}
                                                                style={{ width: '70px', textAlign: 'center' }}
                                                            />
                                                            <Form.Select
                                                                size="sm"
                                                                value={product.duration_type || 'days'}
                                                                onChange={(e) => handleUpdateProductField(product.id, 'duration_type', e.target.value)}
                                                            >
                                                                {DURATION_TYPE_OPTIONS.map(opt => (
                                                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                                ))}
                                                            </Form.Select>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <Form.Select
                                                            size="sm"
                                                            value={product.time_per_day || 'morning'}
                                                            onChange={(e) => handleUpdateProductField(product.id, 'time_per_day', e.target.value)}
                                                        >
                                                            {TIME_PER_DAY_OPTIONS.map(opt => (
                                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                            ))}
                                                        </Form.Select>
                                                    </td>
                                                    <td className="text-center">
                                                        <Form.Check
                                                            type="checkbox"
                                                            checked={product.in_prescription === 1}
                                                            onChange={(e) =>
                                                                handleUpdateProductField(
                                                                    product.id,
                                                                    'in_prescription',
                                                                    e.target.checked ? 1 : 0
                                                                )
                                                            }
                                                        />
                                                    </td>
                                                    <td className="text-center">
                                                        <Button
                                                            variant="danger"
                                                            size="sm"
                                                            onClick={() => handleRemoveProduct(product.id)}
                                                        >
                                                            <FaTrashAlt size={12} />
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </div>

                        {/* Note Field */}
                        <div>
                            <Form.Label className="fw-bold">
                                <FaFileAlt className="me-2" /> {t('Note')}
                            </Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                value={generalNote}
                                onChange={(e) => setGeneralNote(e.target.value)}
                                placeholder={t('Add any additional notes here...')}
                            />
                        </div>
                    </Modal.Body>

                    <Modal.Footer className="p-2">
                        <Button
                            variant="secondary"
                            onClick={() => setShowModal(false)}
                        >
                            {t('cancel')}
                        </Button>
                        <Button
                            type="submit"
                            variant="primary"
                            disabled={submitting || selectedProducts.filter(p => p.name && p.name.trim() !== '').length === 0}
                        >
                            {submitting ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    {t('saving')}
                                </>
                            ) : (
                                editingPrescription ? t('update') : t('save')
                            )}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
    );
}